import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { startWith, switchMap } from 'rxjs';
import { getInspectionEntryStatusFromFormGroup, InspectionEntryFormType, isTgaRelevant } from '../../../inspection-util';
import { ConfiguredActionItemDto } from '@artemis-software/wr-api';
import { InspectionContext } from '@pages/inspection-detail-page-v2/inspection.context';
import SeverityEnum = ConfiguredActionItemDto.SeverityEnum;

@Component({
  standalone: false,
  selector: 'wr-inspection-entry-list-item[inspectionEntry]',
  templateUrl: './inspection-entry-list-item.component.html',
  styleUrls: ['./inspection-entry-list-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InspectionEntryListItemComponent {

  readonly inspectionContext = inject(InspectionContext);

  readonly selected = input<boolean>(false);

  readonly canEdit = input<boolean>(false);

  readonly inspectionEntry = input.required<InspectionEntryFormType>();

  readonly markPostProcessingDone = output<void>();

  private readonly entryChanged = toSignal(toObservable(this.inspectionEntry).pipe(
    switchMap(entry => entry.valueChanges.pipe(startWith(null))),
  ));

  readonly isImminentDanger = computed(() => {
    this.entryChanged();
    return this.inspectionEntry().controls.action.value?.severity === SeverityEnum.ImminentDanger;
  });

  readonly equipmentCount = computed(() => {
    this.entryChanged();
    return this.inspectionEntry().controls.buildingEquipments.value?.length ?? 0;
  });

  readonly tgaRelevant = computed(() => {
    this.entryChanged();
    return isTgaRelevant(this.inspectionEntry());
  });

  readonly markedForPostProcessing = computed(() => {
    this.entryChanged();
    return this.inspectionEntry().controls.markedForPostProcessing.value;
  });

  protected readonly getInspectionEntryStatusFromFormGroup = getInspectionEntryStatusFromFormGroup;

  onMarkPostProcessingDoneClick(event: MouseEvent): void {
    if (!this.canEdit()) {
      return;
    }
    event.stopPropagation();
    this.markPostProcessingDone.emit();
  }
}
